/**
 * Campaign history — types + diff utility
 * =========================================
 *
 * One history entry is written per save event on a campaign. Each entry
 * carries an actor snapshot (who did it) + a list of structured changes.
 * Storage lives at /organizations/{sourceOrgId}/jobs/{jobId}/history/{entryId}.
 *
 * We deliberately keep the log client-side + best-effort: a failed write
 * should never block the actual campaign save. The activity panel will
 * show whatever landed. If drift becomes a real problem, a Cloud Function
 * trigger on /jobs writes could take over.
 */

export type CampaignChange =
  | {
      kind: 'campaign_created';
      client: string | null;
      campaign: string | null;
    }
  | {
      kind: 'header_field';
      /** Machine-friendly field key, e.g. "materialDeadline", "studioName". */
      field: string;
      oldValue: string | null;
      newValue: string | null;
    }
  | {
      kind: 'station_field';
      /** Flighting code — the human-readable row identifier. */
      stationRef: string;
      broadcaster: string | null;
      mediaOwner: string | null;
      /** Machine-friendly field key. */
      field: string;
      oldValue: string | null;
      newValue: string | null;
    }
  | {
      kind: 'station_added';
      stationRef: string;
      broadcaster: string | null;
      mediaOwner: string | null;
    }
  | {
      kind: 'station_removed';
      stationRef: string;
      broadcaster: string | null;
      mediaOwner: string | null;
    }
  | {
      kind: 'link_added';
      stationRef: string;
      broadcaster: string | null;
      mediaOwner: string | null;
      trackId: string;
      trackName: string | null;
    }
  | {
      kind: 'link_removed';
      stationRef: string;
      broadcaster: string | null;
      mediaOwner: string | null;
      trackId: string;
      trackName: string | null;
    };

export interface CampaignHistoryEntry {
  id: string;
  timestamp: number;
  actorUid: string;
  actorName: string;
  actorOrgId: string;
  actorOrgName: string;
  /** Which role the actor's org plays on this campaign. Derived at write
   *  time from the campaign doc's copyRole (or `null` for source). */
  actorOrgRole: 'source' | 'studio' | 'agency' | 'creator';
  changes: CampaignChange[];
}

// Header fields we track for diffs. Keys mirror ParsedChaseListHeader.
const HEADER_TRACKED_FIELDS = [
  'client',
  'campaign',
  'period',
  'date',
  'revision',
  'studioOrgId',
  'studioName',
  'agencyOrgId',
  'agencyName',
] as const;

// Per-station fields we track for diffs. Flighting code changes are
// handled specially since it's also the row identifier.
const STATION_TRACKED_FIELDS = [
  'flightingCode',
  'materialDeadline',
  'flightingDate',
  'language',
  'duration',
  'contact',
  'contactEmail',
  'description',
  'station',
  'mediaOwner',
  'title',
] as const;

function normalise(value: any): string | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    // e.g. languageOptions [{label, value}, ...] → stable label list.
    return value
      .map((v) => (typeof v === 'string' ? v : v && (v.label || v.value || v.name)) || '')
      .filter(Boolean)
      .join(', ') || null;
  }
  if (typeof value === 'object') {
    // e.g. client { label, value }.
    return String((value.label || value.value || value.name) || '').trim() || null;
  }
  return null;
}

interface StationLike {
  station?: string;
  mediaOwner?: string;
  flightingCode?: string;
  linkedTrackId?: string | null;
  [key: string]: any;
}

interface JobLike {
  client?: any;
  campaign?: string | null;
  period?: string | null;
  date?: string | null;
  revision?: string | null;
  studioOrgId?: string | null;
  studioName?: string | null;
  agencyOrgId?: string | null;
  agencyName?: string | null;
  stations?: StationLike[];
}

/**
 * Compute the set of changes between two campaign snapshots.
 *
 * Station identity resolution: we match by (station, mediaOwner, flightingCode)
 * to survive re-ordering. Falls back to index if the triple isn't unique.
 * Flighting code changes on an otherwise-identical station are reported as
 * a `station_field` change on `flightingCode` — NOT as add + remove.
 *
 * `trackNames` is an optional dictionary (trackId → trackName) used to
 * enrich link_added / link_removed entries with a human-readable name.
 */
export function computeCampaignDiff(
  before: JobLike | null | undefined,
  after: JobLike | null | undefined,
  trackNames: Record<string, string | null | undefined> = {},
): CampaignChange[] {
  const changes: CampaignChange[] = [];
  if (!before || !after) return changes;

  // Header diffs.
  for (const key of HEADER_TRACKED_FIELDS) {
    const oldValue = normalise((before as any)[key]);
    const newValue = normalise((after as any)[key]);
    if (oldValue !== newValue) {
      changes.push({ kind: 'header_field', field: key, oldValue, newValue });
    }
  }

  // Station diffs.
  const beforeStations = Array.isArray(before.stations) ? before.stations : [];
  const afterStations = Array.isArray(after.stations) ? after.stations : [];

  // Signature = triple that uniquely (usually) identifies a station.
  // Used as a fallback pairing key for legacy stations that pre-date
  // the per-row `id`.
  const sigOf = (s: StationLike) => [s.station || '', s.mediaOwner || '', s.flightingCode || ''].join('||');
  const beforeByIndex = beforeStations.map((s, i) => ({ station: s, index: i, sig: sigOf(s) }));
  const afterByIndex = afterStations.map((s, i) => ({ station: s, index: i, sig: sigOf(s) }));

  const beforeUnmatched = new Set(beforeByIndex.map((e) => e.index));
  const afterUnmatched = new Set(afterByIndex.map((e) => e.index));
  const pairs: Array<{ b: StationLike; a: StationLike; bIdx: number; aIdx: number }> = [];

  // PASS 1 — pair by stable per-row `id`. Every station saved via
  // ChaseListUploader gets a UUID (backfilled for legacy rows on first
  // save), so id-matching is exact and survives every kind of field
  // edit. Handles the common bug where duplicate signatures (e.g.
  // multiple language variants of the same station, or Radio Retail
  // rows with "CODE: Please advise" everywhere) collapsed the
  // signature pass and emitted phantom station_added/station_removed
  // pairs — which in turn spuriously bounced delivered campaigns to
  // draft on header-only saves.
  //
  // Requires exactly-1 count on both sides, same discipline as the
  // signature pass, so any pathological duplicate-id data falls
  // through to the later passes safely.
  const idOf = (s: StationLike): string | null => {
    const id = (s as any)?.id;
    return typeof id === 'string' && id.length > 0 ? id : null;
  };
  const beforeIdCounts = new Map<string, number>();
  const afterIdCounts = new Map<string, number>();
  for (const e of beforeByIndex) {
    const id = idOf(e.station);
    if (id) beforeIdCounts.set(id, (beforeIdCounts.get(id) || 0) + 1);
  }
  for (const e of afterByIndex) {
    const id = idOf(e.station);
    if (id) afterIdCounts.set(id, (afterIdCounts.get(id) || 0) + 1);
  }
  for (const b of beforeByIndex) {
    const bId = idOf(b.station);
    if (!bId) continue;
    if ((beforeIdCounts.get(bId) || 0) !== 1) continue;
    if ((afterIdCounts.get(bId) || 0) !== 1) continue;
    const a = afterByIndex.find((x) => idOf(x.station) === bId);
    if (!a) continue;
    pairs.push({ b: b.station, a: a.station, bIdx: b.index, aIdx: a.index });
    beforeUnmatched.delete(b.index);
    afterUnmatched.delete(a.index);
  }

  // PASS 2 — signature match on whatever's left. Same as before:
  // catches legacy stations without ids, and would still work for
  // id-less imports.
  const beforeSigCounts = new Map<string, number>();
  const afterSigCounts = new Map<string, number>();
  for (const e of beforeByIndex) {
    if (!beforeUnmatched.has(e.index)) continue;
    beforeSigCounts.set(e.sig, (beforeSigCounts.get(e.sig) || 0) + 1);
  }
  for (const e of afterByIndex) {
    if (!afterUnmatched.has(e.index)) continue;
    afterSigCounts.set(e.sig, (afterSigCounts.get(e.sig) || 0) + 1);
  }

  for (const b of beforeByIndex) {
    if (!beforeUnmatched.has(b.index)) continue;
    if ((beforeSigCounts.get(b.sig) || 0) !== 1) continue;
    if ((afterSigCounts.get(b.sig) || 0) !== 1) continue;
    const a = afterByIndex.find((x) => afterUnmatched.has(x.index) && x.sig === b.sig);
    if (!a) continue;
    pairs.push({ b: b.station, a: a.station, bIdx: b.index, aIdx: a.index });
    beforeUnmatched.delete(b.index);
    afterUnmatched.delete(a.index);
  }

  // For anything left unmatched, try (station, mediaOwner) — this catches
  // flighting-code renames on an otherwise stable row.
  const pairOn = (keys: (keyof StationLike)[]) => {
    const sig = (s: StationLike) => keys.map((k) => s[k] || '').join('||');
    const bMap = new Map<string, number[]>();
    for (const idx of Array.from(beforeUnmatched)) {
      const key = sig(beforeStations[idx]);
      const list = bMap.get(key) || [];
      list.push(idx);
      bMap.set(key, list);
    }
    for (const idx of Array.from(afterUnmatched)) {
      const key = sig(afterStations[idx]);
      const bIdxs = bMap.get(key);
      if (bIdxs && bIdxs.length > 0) {
        const bIdx = bIdxs.shift()!;
        pairs.push({ b: beforeStations[bIdx], a: afterStations[idx], bIdx, aIdx: idx });
        beforeUnmatched.delete(bIdx);
        afterUnmatched.delete(idx);
      }
    }
  };
  pairOn(['station', 'mediaOwner']);

  // Field-level diffs for paired stations.
  for (const { b, a } of pairs) {
    const stationRef = a.flightingCode || b.flightingCode || '(no code)';
    const broadcaster = a.station || b.station || null;
    const mediaOwner = a.mediaOwner || b.mediaOwner || null;
    for (const field of STATION_TRACKED_FIELDS) {
      const oldValue = normalise((b as any)[field]);
      const newValue = normalise((a as any)[field]);
      if (oldValue !== newValue) {
        changes.push({
          kind: 'station_field',
          stationRef,
          broadcaster,
          mediaOwner,
          field,
          oldValue,
          newValue,
        });
      }
    }
    // Link change.
    const oldLink = normalise(b.linkedTrackId);
    const newLink = normalise(a.linkedTrackId);
    if (oldLink !== newLink) {
      if (oldLink && !newLink) {
        changes.push({
          kind: 'link_removed',
          stationRef,
          broadcaster,
          mediaOwner,
          trackId: oldLink,
          trackName: trackNames[oldLink] || null,
        });
      } else if (!oldLink && newLink) {
        changes.push({
          kind: 'link_added',
          stationRef,
          broadcaster,
          mediaOwner,
          trackId: newLink,
          trackName: trackNames[newLink] || null,
        });
      } else if (oldLink && newLink) {
        // Re-link (same station, different track).
        changes.push({
          kind: 'link_removed',
          stationRef,
          broadcaster,
          mediaOwner,
          trackId: oldLink,
          trackName: trackNames[oldLink] || null,
        });
        changes.push({
          kind: 'link_added',
          stationRef,
          broadcaster,
          mediaOwner,
          trackId: newLink,
          trackName: trackNames[newLink] || null,
        });
      }
    }
  }

  // Removed stations (in before but not paired).
  for (const idx of Array.from(beforeUnmatched)) {
    const s = beforeStations[idx];
    changes.push({
      kind: 'station_removed',
      stationRef: s.flightingCode || '(no code)',
      broadcaster: s.station || null,
      mediaOwner: s.mediaOwner || null,
    });
  }
  // Added stations (in after but not paired).
  for (const idx of Array.from(afterUnmatched)) {
    const s = afterStations[idx];
    changes.push({
      kind: 'station_added',
      stationRef: s.flightingCode || '(no code)',
      broadcaster: s.station || null,
      mediaOwner: s.mediaOwner || null,
    });
  }

  return changes;
}

/**
 * Render a single change into a human-readable one-liner. Used by the
 * Activity panel. The active-form phrasing keeps the log readable in a
 * timeline layout.
 */
export function describeChange(change: CampaignChange): string {
  const stationSuffix = (c: any) => {
    const bits: string[] = [];
    if (c.broadcaster) bits.push(c.broadcaster);
    if (c.mediaOwner) bits.push(c.mediaOwner);
    return bits.length > 0 ? ` (${bits.join(' · ')})` : '';
  };
  const fieldLabel = (key: string): string => {
    const map: Record<string, string> = {
      client: 'client',
      campaign: 'campaign name',
      period: 'period',
      date: 'date',
      revision: 'revision',
      studioOrgId: 'studio',
      studioName: 'studio name',
      agencyOrgId: 'agency',
      agencyName: 'agency name',
      flightingCode: 'flighting code',
      materialDeadline: 'material deadline',
      flightingDate: 'flighting date',
      language: 'language',
      duration: 'duration',
      contact: 'contact',
      contactEmail: 'contact email',
      description: 'description',
      station: 'broadcaster',
      mediaOwner: 'media owner',
      title: 'title',
    };
    return map[key] || key;
  };
  const quote = (v: string | null) => (v == null ? 'empty' : `"${v}"`);
  switch (change.kind) {
    case 'campaign_created':
      return `Created campaign${change.campaign ? ` "${change.campaign}"` : ''}${change.client ? ` for ${change.client}` : ''}`;
    case 'header_field':
      return `Changed ${fieldLabel(change.field)} from ${quote(change.oldValue)} to ${quote(change.newValue)}`;
    case 'station_field':
      return `For ${change.stationRef}${stationSuffix(change)}: changed ${fieldLabel(change.field)} from ${quote(change.oldValue)} to ${quote(change.newValue)}`;
    case 'station_added':
      return `Added station ${change.stationRef}${stationSuffix(change)}`;
    case 'station_removed':
      return `Removed station ${change.stationRef}${stationSuffix(change)}`;
    case 'link_added':
      return `Linked ${change.stationRef}${stationSuffix(change)} → ${change.trackName ? `track "${change.trackName}"` : `track ${change.trackId}`}`;
    case 'link_removed':
      return `Unlinked ${change.stationRef}${stationSuffix(change)} from ${change.trackName ? `track "${change.trackName}"` : `track ${change.trackId}`}`;
  }
}
